import { RouteReassignment } from './hub'
import { IMechanicalInformation, MechanicalInformationRequestBody } from './mechanicalInformation'
import { IOfficialReport, OfficialReportRequestBody } from './officialReport'
import { IUserShift } from './user'

export enum MileageLossCode {
    S = 'S',
    OD = 'OD',
    OND = 'OND',
    NSB = 'NSB',
    E = 'E',
    T = 'T',
    M = 'M',
}

export enum DisruptionLogCode {
    Disruption = 'disruption',
    Cleared = 'cleared',
}

export enum DailyLogCode {
    Report = 'Report',
    Info = 'I',
    RouteTransfer = 'RT',
    CodeRed = 'CR',
    CodeBlue = 'CB',
    Broadcast = 'BC',
    Handover = 'HO',
    Incident = 'INC',
}

export const EXTRA_INFO_CODES: string[] = [
    DailyLogCode.Info,
    DailyLogCode.RouteTransfer,
    DailyLogCode.Broadcast,
    DailyLogCode.Handover,
]

export enum CodeType {
    DailyLog = 'DailyLog',
    MileageLoss = 'MileageLoss',
}

export enum RouteSelectionMode {
    None = 'None',
    Default = 'Default',
    All = 'All',
}

export interface Code {
    code: string
    type: CodeType
    description: string
    reasons: CodeTypeReasonInfo[]
}

export type CodeTypeId = 'ML' | 'DL'

export interface CodeTypeReasonInfo {
    reasonId: string
    reasonDescription: string
    isOfficialReportRequired: boolean
    isMechanicalInfoRequired: boolean
    isUrgent: boolean
}

export enum LogSource {
    Automatic = 'Automatic',
    Manual = 'Manual',
}

export enum LogStatus {
    Incomplete = 'Incomplete',
    Completed = 'Completed',
}

export enum LogLocationType {
    CRT = 'CRT',
    All = 'All',
}

export type FlagState = 'Unfinished' | 'Done'

export interface LogUnfinishedMetadata {
    id: number
    comment: string
    author: string
    createdAt: string
    flag: boolean
    flagState: FlagState
}

export type LogPermission = 'View' | 'Create' | 'Update' | 'Delete' | 'Duplicate'

type CodePeriodLogPermissions = 'View' | 'Create' | 'Update' | 'Delete' | 'Flag' | 'Lock' | 'Unlock' | 'Duplicate'
type CodingPeriod = 'LogCreation' | 'LogReview' | 'CodingPeriod' | 'Deadline' | 'Exceptions' | 'PostExceptions'

export interface CodingPeriodInfo {
    codingPeriod: CodingPeriod
    permissions: CodePeriodLogPermissions[]
    deadline: string
}

export interface Log {
    author: string
    originalAuthor?: string
    code: string
    delayInMinutes: number
    descriptionInfo: {
        description: string
        extraInfo: string
        strategy: string
    }
    duty: string[]
    fleet: string[]
    id: string
    linkedReport: string
    locationFrom: string
    locationTo: string
    locationType: LogLocationType
    logType: string
    mileage: number
    isZeroMileage: boolean | null
    reference: string
    routes: string[]
    routeSelectionMode: RouteSelectionMode
    run: string
    timestamp: string
    lastChangedAt: string
    createdAt: string
    trip: string
    rtnTrips: Pick<IRtnTripAsString, 'trip' | 'order'>[]
    report: IOfficialReport | null
    mechanicalInfo: IMechanicalInformation | null
    userShift: IUserShift
    originalTrip: {
        routes?: string[]
        trip?: string
        rtnTrips?: Pick<IRtnTripAsString, 'trip' | 'order'>[]
        run?: string
        duty?: string[]
        fleet?: string[]
    } | null
    version: number
    workstation: string
    garageCode?: string
    codeTypeReasonInfo: CodeTypeReasonInfo | null
    eventLog: null | Log
    direction: string
    source: LogSource
    status: LogStatus
    isUpdatedAfterUnfinished: boolean
    unfinishedFlagMetadata: LogUnfinishedMetadata | null
    isLocked: boolean
    codingPeriodInfo: CodingPeriodInfo
    missingFields: string[]
    propertiesChanged: string[]
    unfinishedVersion: UpdatedLog
    bulkCurtailment: BulkCurtailment | null
    // Client side field to mark log which was reassigned
    reassignedRoute?: RouteReassignment
    unfinishedType?: UpdatedRecordsType
}

export interface BulkCurtailment {
    tripTo: string
    runTo: string
    dutyTo: string
    fleetTo: string
}

export enum UpdatedRecordsType {
    New = 'new',
    Unfinished = 'unfinished',
    MissingInfo = 'missingInfo',
    MissingOfficialReport = 'missingOfficialReport',
    Flagged = 'Flagged',
    ViewInfo = 'ViewInfo',
    ReportUpdated = 'ReportUpdated',
    ReportSent = 'ReportSent',
}

export interface UpdatedLog extends Log {
    groupId?: string
}

export interface IBaseRtnTrip {
    id: string
    route: string
    run: string
    direction: string
    fleetNumber: string
    order: number
}

// When creating logs, the backend expects rtn trip number as a string
export interface IRtnTripAsString extends IBaseRtnTrip {
    duty: string
    trip: string
}

// When retrieving trips from the /controller-logs/extra-info endpoint, trip number is a number
export interface IRtnTripAsNumber extends IBaseRtnTrip {
    duty: string[]
    trip: number
    depotCode: string
}

export enum SortType {
    Time = 'time',
    Urgency = 'urgency',
    Routes = 'routes',
}

export interface Filter {
    type: FilterType
    value?: string[]
    codesMap: Record<string, CodeType>
}

export enum FilterType {
    All = 'All',
    DailyLog = 'DailyLog',
    MileageLoss = 'MileageLoss',
}

export interface BusStop {
    miles: number
    route: string
    stopNumber: string
    stopName: string
    direction: string
    stopOrder: number
    isCurtailmentStop: boolean
    isNearLowBridge: boolean
}

export interface RouteBusStops {
    route: string
    inbound: BusStop[]
    outbound: BusStop[]
}

export enum MandatoryLogInformation {
    Route = 'routes',
    Code = 'code',
    Description = 'description',
    Trip = 'trip',
    Run = 'run',
    Duty = 'duty',
    Fleet = 'fleet',
    LocationFrom = 'locationFrom',
    LocationTo = 'locationTo',
    ExtraInfo = 'extraInfo',
}

export const ReportReasonsIds = {
    Incident: ['OR001'],
    LateTakeover: ['OD010', 'OR005'],
    LateDeparture: ['OR004'],
    Headway: ['OR002'],
    OnTime: ['OR003'],
}

export interface ICreateControllerLogRequest {
    id: string
    reference?: string
    code?: string
    codeTypeId?: string
    routes?: string[]
    routeSelectionMode?: RouteSelectionMode
    trip?: string
    rtnTrips?: IRtnTripAsString[]
    run?: string
    duty?: string[]
    fleet?: string[]
    locationFrom?: string
    locationTo?: string
    locationType?: LogLocationType
    description?: string
    extraInfo?: string
    strategy?: string
    delay?: number | string
    mileage?: number
    isZeroMileage?: boolean
    author?: string
    timeStamp?: string | null
    workstation?: string
    garageCode?: string
    report?: OfficialReportRequestBody | null
    mechanicalInfo?: MechanicalInformationRequestBody | null
    originalTrip?: {
        routes?: string[]
        trip?: string
        rtnTrips?: IRtnTripAsString[]
        run?: string
        duty?: string[]
        fleet?: string[]
    }
    eventLogId?: string
    selectedCodeTypeReasonId?: string
    direction?: string
    bulkCurtailment?: BulkCurtailment
}

export interface ICreateControllerLogResponse {
    id: string
}

export interface ICreateMileageLossRequest
    extends Pick<
        Log,
        'routes' | 'routeSelectionMode' | 'trip' | 'rtnTrips' | 'run' | 'duty' | 'fleet' | 'originalTrip'
    > {
    id: string
    reference?: string
    timeStamp: string | null
    workstation?: string
    eventLogId?: string
    author?: string
}
